/**
 * SunoStyleSheet — шторка с редактором стиля + Apply/Cancel.
 *
 * Это «транспортный» компонент: сама UI-логика живёт в
 * `SunoStyleEditor` (переиспользуемый), а эта шторка только
 * оборачивает его в Sheet и буферизирует изменения до нажатия
 * «Применить».
 *
 * Используется в двух местах:
 *   1. `SunoStyleButton` — pill в виджете
 *   2. `MobileSettingsSheet` — строка «Стиль: ...» открывает шторку
 *      поверх settings-sheet (nested sheets работают через portal,
 *      специальная логика не нужна)
 */
import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import Sheet from '../../../components/Sheet'
import SpotlightTour from '../../../components/SpotlightTour'
import SunoStyleEditor from './SunoStyleEditor'
import {
	buildStyleSheetTourSteps,
	SUNO_STYLE_SHEET_TOUR_KEY,
} from '../../../data/widgetTourSteps'
import type { Model } from '../../../data/models'

interface Props {
	open: boolean
	onClose: () => void
	model: Model | null
	currentStyle: string
	currentNegativeTags: string
	onApply: (result: { style: string; negativeTags: string }) => void
	/** Заголовок шторки — по умолчанию «Стиль песни», меняется под контекст
	 *  параметрической операции (напр. «Описание инструментала»). */
	title?: string
	/** Лимит длины style-поля для текущей операции (пробрасывается в Editor).
	 *  По умолчанию 1000. */
	styleMaxLength?: number
}

export default function SunoStyleSheet({
	open,
	onClose,
	model,
	currentStyle,
	currentNegativeTags,
	onApply,
	title = 'Стиль песни',
	styleMaxLength,
}: Props) {
	const [style, setStyle] = useState('')
	const [negativeTags, setNegativeTags] = useState('')
	// Tour-стейт: авто-показ на первом открытии (если юзер не проходил),
	// + перезапуск через `?` help-кнопку в header'е.
	const [tourOpen, setTourOpen] = useState(false)

	// При открытии — копируем в локальный буфер. «Отмена» не мутирует виджет.
	useEffect(() => {
		if (!open) return
		setStyle(currentStyle)
		setNegativeTags(currentNegativeTags)
	}, [open, currentStyle, currentNegativeTags])

	// Авто-показ тура на первом открытии. Небольшой delay чтобы Sheet
	// закончил fade-in анимацию и targets уже были в DOM.
	useEffect(() => {
		if (!open) return
		if (typeof window === 'undefined') return
		if (window.localStorage.getItem(SUNO_STYLE_SHEET_TOUR_KEY)) return
		const t = setTimeout(() => setTourOpen(true), 350)
		return () => clearTimeout(t)
	}, [open])

	const handleTourFinish = useCallback(() => {
		try {
			window.localStorage.setItem(SUNO_STYLE_SHEET_TOUR_KEY, '1')
		} catch {
			// localStorage может быть отключён — не критично, тур просто
			// покажется снова в следующий раз
		}
		setTourOpen(false)
	}, [])

	const handleRestartTour = useCallback(() => {
		setTourOpen(true)
	}, [])

	const handleApply = useCallback(() => {
		onApply({
			style: style.trim(),
			negativeTags: negativeTags.trim(),
		})
		onClose()
	}, [style, negativeTags, onApply, onClose])

	return (
		<>
			<Sheet
				open={open}
				onClose={onClose}
				variant="center"
				title={title}
				className="suno-style-sheet"
				headerExtra={
					<button
						type="button"
						className="suno-style-sheet__help"
						onClick={handleRestartTour}
						aria-label="Как пользоваться настройками стиля"
						title="Как пользоваться"
					>
						?
					</button>
				}
			>
				<div className="suno-style-sheet__body">
					<SunoStyleEditor
						model={model}
						style={style}
						negativeTags={negativeTags}
						onStyleChange={setStyle}
						onNegativeTagsChange={setNegativeTags}
						styleMaxLength={styleMaxLength}
					/>
				</div>

				<div className="suno-style-sheet__actions">
					{/* «Очистить» слева — один клик чистит оба поля локального буфера
					    (коммит в виджет только после «Применить»). */}
					<button
						type="button"
						className="suno-sheet-btn suno-sheet-btn--ghost"
						onClick={() => {
							setStyle('')
							setNegativeTags('')
						}}
						disabled={!style.trim() && !negativeTags.trim()}
					>
						Очистить
					</button>
					<div className="suno-style-sheet__actions-spacer" />
					<button
						type="button"
						className="suno-sheet-btn suno-sheet-btn--ghost"
						onClick={onClose}
					>
						Отмена
					</button>
					<button
						type="button"
						className="suno-sheet-btn suno-sheet-btn--primary"
						onClick={handleApply}
					>
						Применить
					</button>
				</div>
			</Sheet>

			{/* Тур поверх sheet'а. Рендерим через createPortal к document.body,
			    иначе SpotlightTour (position:fixed) прилипает к ближайшему
			    containing block'у (родителю с transform/filter/backdrop-filter)
			    вместо viewport'а — уходит ПОД Sheet. Portal выносит тур на
			    уровень body, rect становится относительно viewport'а, z-index
			    10100+ гарантирует что тур над Sheet (z-index 10000). */}
			{open && tourOpen && createPortal(
				<SpotlightTour
					steps={buildStyleSheetTourSteps()}
					storageKey={SUNO_STYLE_SHEET_TOUR_KEY}
					onFinish={handleTourFinish}
				/>,
				document.body,
			)}
		</>
	)
}
